import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { format, isSameDay } from "date-fns";
import { Combobox } from "@/components/ui/combobox";
import { toast } from "@/components/ui/use-toast";
import { parseDateFromStorage, formatDateForDisplay } from "@/lib/utils/date-utils";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

interface CourseEvent {
  id: string;
  title: string;
  start_date: string;
  end_date: string | null;
  location: string | null;
  category: string;
  event_type: string;
  status: string;
  start_time?: string;
  end_time?: string;
}

const Schedule = () => {
  const [date, setDate] = useState<Date>(new Date());
  const [events, setEvents] = useState<CourseEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    thisMonth: 0,
    upcoming: 0
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('courses')
        .select('id, title, start_date, end_date, location, category, event_type, status, start_time, end_time')
        .eq('status', 'published')
        .order('start_date', { ascending: true });

      if (error) {
        console.error('Error fetching events:', error);
        toast({
          title: "Error",
          description: "Failed to load schedule events",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        setEvents(data as CourseEvent[]);
        
        // Extract unique categories for filtering
        const uniqueCategories = [...new Set(data.map(event => event.category))];
        setCategories(uniqueCategories);
        // Stats
        const now = new Date();
        const thisMonth = now.getMonth();
        const thisYear = now.getFullYear();
        setStats({
          total: data.length,
          thisMonth: data.filter(e => {
            const d = new Date(e.start_date);
            return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
          }).length,
          upcoming: data.filter(e => new Date(e.start_date) > now).length
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to load schedule events",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter events for the selected date
  const eventsForSelectedDate = date 
    ? events.filter(event => {
        if (!event.start_date) return false;
        const eventDate = parseDateFromStorage(event.start_date);
        return eventDate && isSameDay(eventDate, date);
      })
    : [];

  // Filter events by category if one is selected
  const filteredEvents = selectedCategory 
    ? events.filter(event => event.category === selectedCategory)
    : events;

  // Format category options for the combobox
  const categoryOptions = [
    { value: "", label: "All Categories" },
    ...categories.map(category => ({ value: category, label: category }))
  ];

  // Helper function to get event type badge color
  const getEventTypeColor = (eventType: string) => {
    return eventType === 'live' ? 'bg-[#0f8f8c] text-white' : 'bg-[#b1c3cf] text-[#004161]';
  };

  // Function to render date highlights in the calendar
  const renderDateHighlights = (date: Date) => {
    const hasEvent = events.some(event => 
      event.start_date && isSameDay(new Date(event.start_date), date)
    );

    return hasEvent ? <div className="w-1.5 h-1.5 bg-[#004161] rounded-full absolute bottom-1" /> : null;
  };

  // Format the event time for display
  const formatEventTime = (event: CourseEvent) => {
    if (!event.start_time) return "";
    
    let timeString = format(new Date(`2000-01-01T${event.start_time}`), 'h:mm a');
    
    if (event.end_time) {
      timeString += ` - ${format(new Date(`2000-01-01T${event.end_time}`), 'h:mm a')}`;
    }
    
    return timeString;
  };

  // Add search filter to filteredEvents and eventsForSelectedDate
  const searchedEvents = filteredEvents.filter(event => event.title.toLowerCase().includes(search.toLowerCase()));
  const searchedEventsForSelectedDate = eventsForSelectedDate.filter(event => event.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="w-full max-w-full px-2 md:px-8 py-6 space-y-6">
      {/* Stats Bar */}
      <div className="flex flex-wrap gap-4 items-center justify-between bg-[#f1eceb] border-b-2 border-[#0f8f8c]/30 rounded-xl px-6 py-3 mb-2">
        <div className="font-title text-lg text-[#004161]">Events this month: <span className="font-bold">{stats.thisMonth}</span></div>
        <div className="font-title text-lg text-[#004161]">Upcoming events: <span className="font-bold">{stats.upcoming}</span></div>
        <div className="font-title text-lg text-[#004161]">Total events: <span className="font-bold">{stats.total}</span></div>
      </div>
      <div className="page-header">
        <h1>CEU Event Schedule</h1>
        <p>View and manage your upcoming professional development events</p>
      </div>
      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
        <div className="w-full md:w-1/3">
          <Combobox
            options={categoryOptions}
            value={selectedCategory}
            onChange={setSelectedCategory}
            placeholder="Filter by Category"
          />
        </div>
        <input
          type="text"
          className="w-full md:w-1/3 border border-[#b1c3cf] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f8f8c]"
          placeholder="Search events by name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search events"
        />
      </div>
      <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          <TabsTrigger value="list">List View</TabsTrigger>
        </TabsList>
        {/* Calendar View */}
        <TabsContent value="calendar" className="space-y-4">
          <Card className="shadow-md border-[#b1c3cf]">
            <CardHeader>
              <CardTitle>Calendar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-8 items-start justify-center w-full">
                <div className="flex-1 flex justify-center">
                  <Calendar
                    onChange={(val) => {
                      if (val instanceof Date) setDate(val);
                      else if (Array.isArray(val) && val[0] instanceof Date) setDate(val[0]);
                    }}
                    value={date}
                    tileContent={({ date: calDate, view }) => {
                      if (view === 'month') {
                        const hasEvent = events.some(event => {
                          if (!event.start_date) return false;
                          const eventDate = parseDateFromStorage(event.start_date);
                          return eventDate && isSameDay(eventDate, calDate);
                        });
                        return hasEvent ? <div className="w-2 h-2 bg-[#0f8f8c] rounded-full mt-1 mx-auto" /> : null;
                      }
                      return null;
                    }}
                    tileClassName={({ date: calDate, view }) => {
                      if (view === 'month') {
                        if (isSameDay(calDate, new Date())) {
                          return 'react-calendar-today bg-[#0f8f8c]/10 border-2 border-[#0f8f8c]';
                        }
                        if (date && isSameDay(calDate, date)) {
                          return 'bg-[#0f8f8c] text-white font-bold';
                        }
                      }
                      return '';
                    }}
                    className="rounded-xl border-2 border-[#b1c3cf] text-lg w-full max-w-md shadow-sm"
                    prevLabel="‹"
                    nextLabel="›"
                    showNeighboringMonth={false}
                  />
                </div>
                <div className="flex-1 w-full">
                  <div className="font-title text-xl mb-2 text-[#004161]">Events on {date.toLocaleDateString()}</div>
                  {loading ? (
                    <div className="space-y-3">
                      <div className="h-16 bg-[#b1c3cf]/20 rounded animate-pulse" />
                      <div className="h-16 bg-[#b1c3cf]/20 rounded animate-pulse" />
                    </div>
                  ) : searchedEventsForSelectedDate.length > 0 ? (
                    <div className="space-y-4">
                      {searchedEventsForSelectedDate.map(event => (
                        <Card key={event.id} className="overflow-hidden border border-[#0f8f8c]/20 shadow hover:shadow-lg transition-all">
                          <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-title text-lg text-[#004161]">{event.title}</span>
                                <Badge className={getEventTypeColor(event.event_type)}>{event.event_type === 'live' ? 'Live Event' : 'Self-Paced'}</Badge>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                                <span>{event.start_date && formatDateForDisplay(event.start_date)}</span>
                                {event.start_time && <span>• {formatEventTime(event)}</span>}
                              </div>
                              {event.location && <div className="flex items-center gap-1 text-xs text-[#0f8f8c]"><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" className="inline-block mr-1"><path d="M8 3.5a3.5 3.5 0 0 1 3.5 3.5c0 2.485-3.5 6.5-3.5 6.5S4.5 9.485 4.5 7A3.5 3.5 0 0 1 8 3.5Z"/><circle cx="8" cy="7" r="1"/></svg>{event.location}</div>}
                            </div>
                            <div className="flex flex-col gap-2 items-end min-w-[120px]">
                              <Badge variant="outline" className="bg-[#f1eceb] text-[#004161]">{event.category}</Badge>
                              <button className="px-3 py-1 rounded-lg bg-[#0f8f8c] text-white font-bold hover:bg-[#004161] transition-colors">View Details</button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted-foreground py-8 text-center">No events scheduled for this date.</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {/* List View */}
        <TabsContent value="list">
          <Card className="shadow-md border-[#b1c3cf]">
            <CardHeader>
              <CardTitle>Upcoming Events</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  <div className="h-16 bg-[#b1c3cf]/20 rounded animate-pulse" />
                  <div className="h-16 bg-[#b1c3cf]/20 rounded animate-pulse" />
                </div>
              ) : searchedEvents.length > 0 ? (
                <div className="space-y-4">
                  {searchedEvents.map(event => (
                    <Card key={event.id} className="overflow-hidden border border-[#0f8f8c]/20 shadow hover:shadow-lg transition-all">
                      <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-title text-lg text-[#004161]">{event.title}</span>
                            <Badge className={getEventTypeColor(event.event_type)}>{event.event_type === 'live' ? 'Live Event' : 'Self-Paced'}</Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                            <span>{event.start_date && formatDateForDisplay(event.start_date)}</span>
                            {event.start_time && <span>• {formatEventTime(event)}</span>}
                          </div>
                          {event.location && <div className="flex items-center gap-1 text-xs text-[#0f8f8c]"><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" className="inline-block mr-1"><path d="M8 3.5a3.5 3.5 0 0 1 3.5 3.5c0 2.485-3.5 6.5-3.5 6.5S4.5 9.485 4.5 7A3.5 3.5 0 0 1 8 3.5Z"/><circle cx="8" cy="7" r="1"/></svg>{event.location}</div>}
                        </div>
                        <div className="flex flex-col gap-2 items-end min-w-[120px]">
                          <Badge variant="outline" className="bg-[#f1eceb] text-[#004161]">{event.category}</Badge>
                          <button className="px-3 py-1 rounded-lg bg-[#0f8f8c] text-white font-bold hover:bg-[#004161] transition-colors">View Details</button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-muted-foreground py-8 text-center">No upcoming events found.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Schedule;
